import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, Paper,
  Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { colors } from 'constants/theme'
import { useTranslation } from 'react-i18next'
import AboutUs from 'api/admin/clinic-manage/about.api'
import DashboardConfigsApi, { PromotionGroupBucket, PROMOTION_GROUPS_MAX } from 'api/executive/dashboard-configs.api'
import { usePromotions, PromotionRef } from 'features/executive/shared/hooks/use-reference-data'
import ButtonCustom from 'component/Button/ButtonCustom'
import InputTextField from 'component/Input/InputTextField'
import PageModal from 'new-components/modal/page-modal'
import { notiSuccess, notiError } from 'component/notifications/notifications'
import { icons } from 'constants/images'
import InfoBanner from 'new-components/banners/info-banner'
import PromotionPickerModal from './PromotionPickerModal'
import TabLoading from './TabLoading'

/** promotionIds are conceptually a Set — compare ignoring order. */
const bucketsEqual = (a: PromotionGroupBucket[], b: PromotionGroupBucket[]) => {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i].label !== b[i].label) return false
    if (a[i].promotionIds.length !== b[i].promotionIds.length) return false
    const sa = [...a[i].promotionIds].sort((x, y) => x - y)
    const sb = [...b[i].promotionIds].sort((x, y) => x - y)
    for (let j = 0; j < sa.length; j++) if (sa[j] !== sb[j]) return false
  }
  return true
}

/**
 * ประสิทธิผลโปรโมชั่น (Promotion Groups) — admin defines up to
 * PROMOTION_GROUPS_MAX themed buckets (e.g. "แคมเปญจัดฟัน",
 * "โปรสมาชิก"). Each bucket bundles N promotions.
 *
 * Counting rule (server enforces): a visit (patient × date) that used ANY
 * promotion in the bucket counts as 1 for that bucket. Same patient on a
 * different day = 2. Same patient same day using 3 promos from the bucket
 * = still 1.
 *
 * Constraints:
 *   - bucket.label must be non-empty and unique
 *   - bucket.promotionIds must be non-empty
 *   - each promotionId may appear in at most one bucket (no double-count)
 *   - hard cap of PROMOTION_GROUPS_MAX rows so the dashboard stays readable
 */
export default function PromotionsTab() {
  const { t } = useTranslation()
  const { data: promotions, loading: promosLoading } = usePromotions()
  const [aboutUsId, setAboutUsId] = useState<number | null>(null)
  const [buckets, setBuckets] = useState<PromotionGroupBucket[]>([])
  const [baseline, setBaseline] = useState<PromotionGroupBucket[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [isConfigured, setIsConfigured] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [confirmReset, setConfirmReset] = useState(false)
  /** Index of the bucket whose promotion picker is open, or null when closed. */
  const [pickerForRow, setPickerForRow] = useState<number | null>(null)

  useEffect(() => {
    AboutUs.findAboutUs()
      .then((res) => {
        const id = Number((res?.data as { aboutUsId?: number })?.aboutUsId)
        if (Number.isFinite(id) && id > 0) setAboutUsId(id)
      })
      .catch(() => undefined)
  }, [])

  useEffect(() => {
    if (aboutUsId == null) return
    setLoading(true)
    DashboardConfigsApi.getPromotionGroups(aboutUsId)
      .then((r) => {
        setBuckets(r.buckets)
        setBaseline(r.buckets)
        setIsConfigured(r.isConfigured)
      })
      .catch(() => {
        setBuckets([])
        setBaseline([])
      })
      .finally(() => setLoading(false))
  }, [aboutUsId])

  // id → PromotionRef lookup for hydrating Autocomplete value chips.
  const promoById = useMemo(() => {
    const m = new Map<number, PromotionRef>()
    for (const p of promotions) m.set(p.id, p)
    return m
  }, [promotions])

  const setBucket = (i: number, patch: Partial<PromotionGroupBucket>) => {
    setBuckets((prev) => prev.map((b, idx) => (idx === i ? { ...b, ...patch } : b)))
  }

  const addRow = () => {
    setBuckets((prev) => (prev.length >= PROMOTION_GROUPS_MAX ? prev : [...prev, { label: '', promotionIds: [] }]))
  }

  const removeRow = (i: number) => {
    setBuckets((prev) => prev.filter((_, idx) => idx !== i))
  }

  const moveRow = (i: number, dir: -1 | 1) => {
    setBuckets((prev) => {
      const next = [...prev]
      const j = i + dir
      if (j < 0 || j >= next.length) return prev
      ;[next[i], next[j]] = [next[j], next[i]]
      return next
    })
  }

  const validate = (): string | null => {
    if (buckets.length === 0) return t('DASHBOARD_TARGETS.PROMO.NEED_ONE', 'ต้องมีกลุ่มอย่างน้อย 1 รายการ') as string
    if (buckets.length > PROMOTION_GROUPS_MAX) return `เกินขีดจำกัด (สูงสุด ${PROMOTION_GROUPS_MAX} กลุ่ม)`
    const seenLabels = new Set<string>()
    const seenIds = new Set<number>()
    for (const [i, b] of buckets.entries()) {
      const row = i + 1
      const label = b.label.trim()
      if (!label) return `แถวที่ ${row}: ต้องตั้งชื่อกลุ่ม`
      if (seenLabels.has(label)) return `แถวที่ ${row}: ชื่อกลุ่มซ้ำ "${label}"`
      seenLabels.add(label)
      if (b.promotionIds.length === 0) return `แถวที่ ${row}: เลือกโปรโมชั่นอย่างน้อย 1 รายการ`
      for (const id of b.promotionIds) {
        if (seenIds.has(id)) return `แถวที่ ${row}: โปรโมชั่น "${promoById.get(id)?.name ?? `#${id}`}" อยู่ในกลุ่มอื่นแล้ว`
        seenIds.add(id)
      }
    }
    return null
  }

  const hasUnsavedChanges = useMemo(() => !bucketsEqual(buckets, baseline), [buckets, baseline])

  const saveDisabledReason: string | null = saving
    ? (t('DASHBOARD_TARGETS.SAVE_IN_FLIGHT', { defaultValue: 'กำลังบันทึก...' }) as string)
    : aboutUsId == null
      ? (t('DASHBOARD_TARGETS.NO_ABOUT_US_SHORT', { defaultValue: 'ยังโหลดข้อมูลคลินิกไม่สำเร็จ' }) as string)
      : !hasUnsavedChanges
        ? (t('DASHBOARD_TARGETS.NOTHING_TO_SAVE', { defaultValue: 'ยังไม่มีการเปลี่ยนแปลง' }) as string)
        : null

  /** Roll the in-progress buckets back to whatever was last loaded from
   *  the server. Triggered from the toolbar's "รีเซ็ต" button after the
   *  confirm dialog so an accidental click can't blow away edits. */
  const doReset = () => {
    setBuckets(baseline)
    setValidationError(null)
    notiSuccess(t('DASHBOARD_TARGETS.RESET_SUCCESS', { defaultValue: 'รีเซ็ตการแก้ไขแล้ว' }) as string)
  }

  const save = async () => {
    if (aboutUsId == null) return
    const err = validate()
    if (err) {
      setValidationError(err)
      return
    }
    setValidationError(null)
    setSaving(true)
    try {
      const payload = buckets.map((b) => ({ label: b.label.trim(), promotionIds: b.promotionIds }))
      await DashboardConfigsApi.savePromotionGroups(aboutUsId, payload)
      setBaseline(payload)
      setIsConfigured(true)
      notiSuccess(t('DASHBOARD_TARGETS.SAVE_SUCCESS') as string)
    } catch (e) {
      const msg = (e as any)?.response?.data?.message
      notiError(typeof msg === 'string' ? msg : (t('DASHBOARD_TARGETS.SAVE_ERROR') as string))
    } finally {
      setSaving(false)
    }
  }

  if (loading || promosLoading) {
    return <TabLoading />
  }

  const atMax = buckets.length >= PROMOTION_GROUPS_MAX

  return (
    <Box>
      <InfoBanner sx={{ mb: 2 }}>
        {t(
          'DASHBOARD_TARGETS.PROMO.DESCRIPTION',
          'จัดกลุ่มโปรโมชั่นเป็นแคมเปญที่ฝ่ายการตลาดต้องการติดตาม (สูงสุด ' + PROMOTION_GROUPS_MAX + ' กลุ่ม) — แต่ละกลุ่มรวมโปรโมชั่นที่ต้องการนับเป็นแคมเปญเดียวกัน'
        )}
        {' '}
        {t(
          'DASHBOARD_TARGETS.PROMO.RULE_HINT',
          `กติกาการนับ: 1 visit (คนไข้ × วัน) ใช้โปรในกลุ่มเดียวกันกี่รายการ ก็นับเป็น 1 ครั้งของกลุ่มนั้น — คนเดิมต่างวัน = นับใหม่`
        )}
        {!isConfigured && (
          <> — {t(
            'DASHBOARD_TARGETS.PROMO.NO_CONFIG',
            'ยังไม่ได้ตั้งค่า — แดชบอร์ดกำลังแสดง 1 แถวต่อ 1 โปรโมชั่น กดบันทึกหลังเพิ่มกลุ่มเพื่อให้แดชบอร์ดแสดงแบบจัดกลุ่ม'
          )}</>
        )}
      </InfoBanner>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'flex-end' }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
          <ButtonCustom
            variant="outlined"
            onClick={() => setConfirmReset(true)}
            disabled={saving || !hasUnsavedChanges}
            textButton={t('DASHBOARD_TARGETS.RESET_BUTTON', { defaultValue: 'รีเซ็ต' })}
          />
          <Tooltip title={saveDisabledReason ?? ''} placement="top">
            <span>
              <ButtonCustom
                variant="contained"
                onClick={save}
                disabled={Boolean(saveDisabledReason)}
                textButton={saving ? <CircularProgress size={20} /> : t('DASHBOARD_TARGETS.SAVE')}
              />
            </span>
          </Tooltip>
        </Box>
      </Box>

      {validationError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setValidationError(null)}>
          {validationError}
        </Alert>
      )}

      <Paper sx={{ overflow: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 60 }} align="center">#</TableCell>
              <TableCell sx={{ width: 260 }}>{t('DASHBOARD_TARGETS.PROMO.LABEL', 'ชื่อกลุ่ม / แคมเปญ')}</TableCell>
              <TableCell>{t('DASHBOARD_TARGETS.PROMO.PROMOTIONS', 'โปรโมชั่นที่รวมในกลุ่ม')}</TableCell>
              <TableCell sx={{ width: 120 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell colSpan={4} sx={{ borderBottom: 'none', py: 1 }}>
                <Button
                  onClick={addRow}
                  disabled={atMax}
                  sx={{
                    color: atMax ? 'text.disabled' : colors.themeSecondColor,
                    textTransform: 'none',
                    fontWeight: 500,
                    p: 0,
                    minWidth: 'auto',
                    '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
                  }}
                >
                  <span style={{ marginRight: 4 }}>+</span>
                  {t('DASHBOARD_TARGETS.PROMO.ADD_ROW', 'เพิ่มกลุ่ม')} ({buckets.length}/{PROMOTION_GROUPS_MAX})
                </Button>
              </TableCell>
            </TableRow>
            {buckets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  {t('DASHBOARD_TARGETS.PROMO.EMPTY', 'ยังไม่มีกลุ่ม — กด "เพิ่มกลุ่ม"')}
                </TableCell>
              </TableRow>
            ) : (
              buckets.map((b, i) => {
                // Promotions claimed by *other* rows can't be added here
                // (server enforces "one promo per bucket"). We pass them to
                // the picker as disabled rows rather than hiding them, so
                // the admin sees *why* a promo is unavailable.
                const claimedByOthers: number[] = []
                buckets.forEach((other, oi) => {
                  if (oi === i) return
                  other.promotionIds.forEach((id) => claimedByOthers.push(id))
                })
                const selectedRefs = b.promotionIds
                  .map((id) => promoById.get(id))
                  .filter((p): p is PromotionRef => p != null)
                const MAX_VISIBLE = 2
                const visibleChips = selectedRefs.slice(0, MAX_VISIBLE)
                const overflowCount = selectedRefs.length - visibleChips.length

                return (
                  <TableRow key={i} hover sx={{ verticalAlign: 'top' }}>
                    <TableCell align="center" sx={{ pt: 2 }}>{i + 1}</TableCell>
                    <TableCell sx={{ pt: 1 }}>
                      <InputTextField
                        hookValue
                        inputHeight={37}
                        placeholder="แคมเปญจัดฟัน, โปรสมาชิก, ..."
                        value={b.label}
                        onchange={(e: any) => setBucket(i, { label: e.target.value })}
                      />
                    </TableCell>
                    <TableCell sx={{ pt: 1 }}>
                      <Box
                        onClick={() => setPickerForRow(i)}
                        sx={{
                          minHeight: 37,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          px: 1.5,
                          py: 0.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          cursor: 'pointer',
                          backgroundColor: '#FFFFFF',
                          '&:hover': { borderColor: '#1CB99A' },
                        }}
                      >
                        {selectedRefs.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            {t('DASHBOARD_TARGETS.PROMO.PICKER_EMPTY_PLACEHOLDER', {
                              defaultValue: 'เลือกโปรโมชั่น...',
                            })}
                          </Typography>
                        ) : (
                          <>
                            {visibleChips.map((p) => (
                              <Chip
                                key={p.id}
                                size="small"
                                label={p.name}
                                sx={{
                                  height: 22,
                                  fontSize: '12px',
                                  maxWidth: 180,
                                  '& .MuiChip-label': {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  },
                                }}
                              />
                            ))}
                            {overflowCount > 0 && (
                              <Chip
                                size="small"
                                label={`+${overflowCount}`}
                                sx={{
                                  height: 22,
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  backgroundColor: '#1CB99A1A',
                                  color: '#1CB99A',
                                  flexShrink: 0,
                                }}
                              />
                            )}
                          </>
                        )}
                        <Box sx={{ flexGrow: 1 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                          {selectedRefs.length > 0
                            ? t('DASHBOARD_TARGETS.PROMO.EDIT', { defaultValue: 'แก้ไข' })
                            : ''}
                        </Typography>
                      </Box>
                      {pickerForRow === i && (
                        <PromotionPickerModal
                          open={pickerForRow === i}
                          onClose={() => setPickerForRow(null)}
                          onConfirm={(ids) => {
                            setBucket(i, { promotionIds: ids })
                            setPickerForRow(null)
                          }}
                          promotions={promotions}
                          currentSelectedIds={b.promotionIds}
                          claimedByOthersIds={claimedByOthers}
                          bucketLabel={b.label.trim() || (t('DASHBOARD_TARGETS.PROMO.UNNAMED', { defaultValue: 'กลุ่มที่ ' }) + (i + 1))}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ pt: 1.5 }}>
                      <Box sx={{ display: 'flex' }}>
                        <Tooltip title="เลื่อนขึ้น">
                          <span>
                            <IconButton size="small" disabled={i === 0} onClick={() => moveRow(i, -1)}>↑</IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="เลื่อนลง">
                          <span>
                            <IconButton size="small" disabled={i === buckets.length - 1} onClick={() => moveRow(i, 1)}>↓</IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={t('DASHBOARD_TARGETS.PROMO.REMOVE', 'ลบ') as string}>
                          <IconButton size="small" onClick={() => removeRow(i)}>
                            <img src={icons.btnTrash} alt="delete" style={{ width: 20, height: 20 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </Paper>

      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {t(
            'DASHBOARD_TARGETS.PROMO.HINT',
            `เคล็ดลับ: โปรโมชั่นที่ไม่ได้ใส่ในกลุ่มจะรวมเป็น "ไม่อยู่ในกลุ่ม" บนแดชบอร์ดอัตโนมัติ (ทั้งหมด ${promotions.length} โปรโมชั่นในระบบ)`
          )}
          {hasUnsavedChanges && ` · ${t('DASHBOARD_TARGETS.UNSAVED', 'มีการเปลี่ยนแปลงที่ยังไม่บันทึก')}`}
        </Typography>
      </Box>

      <PageModal
        isOpen={confirmReset}
        size="XS"
        title={t('DASHBOARD_TARGETS.RESET_TITLE', { defaultValue: 'ยืนยันรีเซ็ตการแก้ไข' }) as string}
        handleClose={() => setConfirmReset(false)}
        onCancel={{
          onClick: () => setConfirmReset(false),
          title: t('DASHBOARD_TARGETS.PICKER.CANCEL', { defaultValue: 'ยกเลิก' }) as string,
        }}
        onSubmit={{
          onClick: () => {
            setConfirmReset(false)
            doReset()
          },
          title: t('DASHBOARD_TARGETS.RESET_CONFIRM', { defaultValue: 'รีเซ็ต' }) as string,
          type: 'danger',
        }}
      >
        <Typography variant="body2">
          {t('DASHBOARD_TARGETS.RESET_BODY', {
            defaultValue: 'หมายเหตุ: การเปลี่ยนแปลงที่ยังไม่บันทึกทั้งหมดจะถูกรีเซ็ต',
          })}
        </Typography>
      </PageModal>
    </Box>
  )
}
